import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, Loader2, ShoppingCart, CreditCard, ArrowRight, ArrowLeft, Tag, Shield, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const plans = {
  starter: {
    name: 'Starter',
    monthlyPrice: 299,
    setupFee: 500,
    users: 2,
    features: [
      'Up to 2 team members', 'Unlimited estimates', 'Proposal generation',
      'Digital signatures', 'Branded proposals', 'Email support', '50GB document storage'
    ],
    popular: false
  },
  pro: {
    name: 'Pro',
    monthlyPrice: 499,
    setupFee: 500,
    users: 5,
    features: [
      'Up to 5 team members', 'Everything in Starter', 'No HomeFixer360 branding',
      'Advanced analytics', 'Priority support', 'API access', '200GB document storage', 'Custom templates'
    ],
    popular: true
  },
  enterprise: {
    name: 'Enterprise',
    monthlyPrice: 999,
    setupFee: 500,
    users: -1,
    features: [
      'Unlimited team members', 'Everything in Pro', 'Dedicated account manager',
      'Custom integrations', 'SLA guarantee', 'Advanced security features',
      'Unlimited document storage', 'White-label options'
    ],
    popular: false
  }
};

const SubscriptionCheckout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // If plan is in URL params, skip to cart
  const preselectedPlan = searchParams.get('plan');
  const validPlans = ['starter', 'pro', 'enterprise'];
  const initialPlan = validPlans.includes(preselectedPlan) ? preselectedPlan : 'pro';
  const initialStep = preselectedPlan && validPlans.includes(preselectedPlan) ? 'cart' : 'plan';

  // Steps: 'plan' -> 'cart' -> 'payment'
  const [step, setStep] = useState(initialStep);
  const [selectedPlan, setSelectedPlan] = useState(initialPlan);

  // Customer info
  const [customer, setCustomer] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    company: '', address: '', city: '', state: '', zip: ''
  });

  // Coupon
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  // Payment
  const [loading, setLoading] = useState(false);

  // Derived pricing
  const plan = plans[selectedPlan];

  const calcDiscount = (basePrice) => {
    if (!appliedCoupon) return basePrice;
    if (appliedCoupon.discount_percent) return Math.max(0, basePrice * (1 - appliedCoupon.discount_percent / 100));
    if (appliedCoupon.discount_fixed) return Math.max(0, basePrice - appliedCoupon.discount_fixed);
    return basePrice;
  };

  const monthlyAfterDiscount = calcDiscount(plan.monthlyPrice);
  const setupAfterDiscount = calcDiscount(plan.setupFee);
  const totalDue = monthlyAfterDiscount + setupAfterDiscount;
  const isFree = totalDue === 0;
  const isBeta = appliedCoupon?.grants_beta_access;
  const isLeadProgram = appliedCoupon?.coupon_type === 'lead_program';
  const betaDays = appliedCoupon?.trial_days || 30;

  // Coupon handlers
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) { toast.error('Enter a coupon code'); return; }
    setValidatingCoupon(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/coupons/validate`, { code: couponCode.toUpperCase() });
      if (res.data.valid) {
        setAppliedCoupon(res.data.coupon);
        const txt = res.data.coupon.discount_percent ? `${res.data.coupon.discount_percent}%` :
                    res.data.coupon.discount_fixed ? `$${res.data.coupon.discount_fixed}` : 'Discount';
        toast.success(`Coupon applied! ${txt} off`);
      } else {
        toast.error(res.data.message || 'Invalid coupon code');
      }
    } catch { toast.error('Failed to validate coupon'); }
    finally { setValidatingCoupon(false); }
  };

  const handleRemoveCoupon = () => { setAppliedCoupon(null); setCouponCode(''); };

  // Validation
  const isCartValid = customer.firstName && customer.lastName && customer.email && customer.phone && customer.address && customer.city && customer.state && customer.zip;

  // Proceed to payment
  const handleProceedToPayment = async () => {
    if (!isCartValid) { toast.error('Please fill in all required fields'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/payments/create-subscription-checkout`, {
        tier: selectedPlan,
        coupon_code: appliedCoupon ? couponCode.toUpperCase() : null,
        customer_email: customer.email,
        customer_name: `${customer.firstName} ${customer.lastName}`,
        customer_phone: customer.phone,
        company: customer.company,
        billing_address: {
          line1: customer.address,
          city: customer.city,
          state: customer.state,
          postal_code: customer.zip
        },
        is_free: isFree,
        is_beta: isBeta
      });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else if (res.data.success) {
        toast.success('Subscription activated!');
        setTimeout(() => navigate('/dashboard'), 1500);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed. Please try again.');
    } finally { setLoading(false); }
  };

  const updateField = (field, val) => setCustomer(prev => ({ ...prev, [field]: val }));

  // ─── STEP 1: Plan Selection ────────────────────────────
  if (step === 'plan') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Plan</h1>
            <p className="text-lg text-gray-600">30-Day Money Back Guarantee on all plans</p>
            <Badge className="mt-3 bg-green-600 text-white px-4 py-1">Cancel Anytime</Badge>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {Object.entries(plans).map(([key, p]) => (
              <Card
                key={key}
                className={`cursor-pointer transition-all hover:shadow-xl ${selectedPlan === key ? 'ring-2 ring-blue-600 shadow-xl' : 'hover:ring-1 hover:ring-blue-300'}`}
                onClick={() => setSelectedPlan(key)}
                data-testid={`plan-${key}`}
              >
                <CardHeader className="text-center pb-4">
                  {p.popular && <Badge className="mb-2 bg-orange-500">Most Popular</Badge>}
                  <h3 className="text-2xl font-bold text-gray-900">{p.name}</h3>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-blue-600">${p.monthlyPrice}</span>
                    <span className="text-gray-600">/month</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">+ ${p.setupFee} one-time setup fee</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {p.features.map((f, i) => (
                      <li key={i} className="flex items-start text-sm text-gray-700">
                        <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  {selectedPlan === key && (
                    <div className="mt-4 text-center">
                      <Badge className="bg-blue-600 text-white">Selected</Badge>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button size="lg" className="px-12 py-6 text-lg bg-blue-600 hover:bg-blue-700" onClick={() => setStep('cart')} data-testid="continue-to-cart">
              Continue to Cart <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── STEP 2: Cart (Customer Info + Coupon + Summary) ───
  if (step === 'cart') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <button onClick={() => setStep('plan')} className="text-gray-500 hover:text-gray-800">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <ShoppingCart className="w-7 h-7 text-orange-600" /> Your Cart
              </h1>
              <p className="text-gray-500">Review your order and enter your information</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Left: Customer Info */}
            <div className="lg:col-span-3 space-y-6">
              {/* Customer Information */}
              <Card>
                <CardHeader className="pb-3">
                  <h2 className="text-lg font-semibold">Customer Information</h2>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>First Name *</Label>
                      <Input value={customer.firstName} onChange={e => updateField('firstName', e.target.value)} placeholder="John" data-testid="cart-first-name" />
                    </div>
                    <div>
                      <Label>Last Name *</Label>
                      <Input value={customer.lastName} onChange={e => updateField('lastName', e.target.value)} placeholder="Smith" data-testid="cart-last-name" />
                    </div>
                  </div>
                  <div>
                    <Label>Company Name</Label>
                    <Input value={customer.company} onChange={e => updateField('company', e.target.value)} placeholder="Smith Roofing LLC" data-testid="cart-company" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Email *</Label>
                      <Input type="email" value={customer.email} onChange={e => updateField('email', e.target.value)} placeholder="john@smithroofing.com" data-testid="cart-email" />
                    </div>
                    <div>
                      <Label>Phone *</Label>
                      <Input type="tel" value={customer.phone} onChange={e => updateField('phone', e.target.value)} placeholder="(555) 123-4567" data-testid="cart-phone" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Billing Address */}
              <Card>
                <CardHeader className="pb-3">
                  <h2 className="text-lg font-semibold">Billing Address</h2>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Street Address *</Label>
                    <Input value={customer.address} onChange={e => updateField('address', e.target.value)} placeholder="123 Main St" data-testid="cart-address" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>City *</Label>
                      <Input value={customer.city} onChange={e => updateField('city', e.target.value)} placeholder="Dallas" data-testid="cart-city" />
                    </div>
                    <div>
                      <Label>State *</Label>
                      <Input value={customer.state} onChange={e => updateField('state', e.target.value)} placeholder="TX" maxLength={2} data-testid="cart-state" />
                    </div>
                    <div>
                      <Label>ZIP *</Label>
                      <Input value={customer.zip} onChange={e => updateField('zip', e.target.value)} placeholder="75001" data-testid="cart-zip" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Coupon Code */}
              <Card>
                <CardHeader className="pb-3">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Tag className="w-5 h-5 text-orange-600" /> Coupon Code
                  </h2>
                </CardHeader>
                <CardContent>
                  {!appliedCoupon ? (
                    <div className="flex gap-3">
                      <Input
                        className="flex-1"
                        placeholder="Enter coupon or beta code"
                        value={couponCode}
                        onChange={e => setCouponCode(e.target.value.toUpperCase())}
                        onKeyDown={e => { if (e.key === 'Enter') handleApplyCoupon(); }}
                        disabled={validatingCoupon}
                        data-testid="coupon-input"
                      />
                      <Button onClick={handleApplyCoupon} disabled={validatingCoupon || !couponCode.trim()} data-testid="apply-coupon-btn">
                        {validatingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 p-4 rounded-lg">
                      <div>
                        <p className="font-semibold text-green-700">{appliedCoupon.code} applied!</p>
                        <p className="text-sm text-green-600">
                          {appliedCoupon.grants_beta_access
                            ? `${appliedCoupon.trial_days || 30}-day beta access`
                            : appliedCoupon.discount_percent ? `${appliedCoupon.discount_percent}% off`
                            : appliedCoupon.discount_fixed ? `$${appliedCoupon.discount_fixed} off`
                            : 'Discount applied'}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={handleRemoveCoupon}>Remove</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right: Order Summary */}
            <div className="lg:col-span-2">
              <div className="sticky top-8">
                <Card className="border-2 border-blue-200">
                  <CardHeader className="bg-blue-50 pb-3">
                    <h2 className="text-lg font-bold text-gray-900">Order Summary</h2>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{plan.name} Plan</span>
                      <span className="font-semibold">${plan.monthlyPrice}/mo</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Setup Fee</span>
                      <span className="font-semibold">${plan.setupFee}</span>
                    </div>

                    {appliedCoupon && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount ({appliedCoupon.code})</span>
                        <span className="font-semibold">
                          {appliedCoupon.discount_percent ? `-${appliedCoupon.discount_percent}%` :
                           appliedCoupon.discount_fixed ? `-$${appliedCoupon.discount_fixed}` : 'Free'}
                        </span>
                      </div>
                    )}

                    <div className="border-t pt-3 mt-2">
                      {appliedCoupon && (
                        <div className="space-y-1 mb-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Monthly after discount</span>
                            <span>{monthlyAfterDiscount === 0 ? 'FREE' : `$${monthlyAfterDiscount.toFixed(2)}/mo`}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Setup after discount</span>
                            <span>{setupAfterDiscount === 0 ? 'FREE' : `$${setupAfterDiscount.toFixed(2)}`}</span>
                          </div>
                        </div>
                      )}
                      <div className="flex justify-between text-xl font-bold">
                        <span>Total Due</span>
                        <span className="text-blue-600">{isFree ? '$0.00' : `$${totalDue.toFixed(2)}`}</span>
                      </div>
                    </div>

                    {/* Free / Beta / Lead Program notices */}
                    {isFree && !isBeta && !isLeadProgram && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <p className="font-semibold text-amber-800">$1.00 Card Verification Required</p>
                            <p className="text-amber-700 mt-1">
                              To activate your account, a one-time $1.00 charge will be made to verify your payment method. This is not a subscription charge.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {isBeta && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                        <div className="flex items-start gap-2">
                          <Shield className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <p className="font-semibold text-blue-800">Beta Access — {betaDays} Days</p>
                            <p className="text-blue-700 mt-1">
                              Your beta access is valid for <strong>{betaDays} days</strong>. Your subscription will <strong>not be charged automatically</strong>.
                            </p>
                            <p className="text-blue-700 mt-1">
                              We will alert you a few days before your beta period ends and allow you to start your subscription at that time.
                            </p>
                            {isFree && (
                              <div className="mt-2 pt-2 border-t border-blue-200">
                                <p className="text-amber-700 font-medium">
                                  A one-time $1.00 card verification charge is required to activate your account. This is not a subscription charge.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {isLeadProgram && !isBeta && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
                        <div className="flex items-start gap-2">
                          <Shield className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <p className="font-semibold text-green-800">Lead Program — Free Subscription</p>
                            <p className="text-green-700 mt-1">
                              Your subscription is included with your lead program purchase. A $1.00 card verification charge is required to activate your account.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <Button
                      size="lg"
                      className="w-full mt-4 py-5 text-base bg-orange-600 hover:bg-orange-700"
                      onClick={handleProceedToPayment}
                      disabled={loading || !isCartValid}
                      data-testid="proceed-to-payment-btn"
                    >
                      {loading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                      ) : (
                        <><CreditCard className="w-4 h-4 mr-2" /> {isFree ? 'Verify Card ($1.00)' : `Pay $${totalDue.toFixed(2)}`}</>
                      )}
                    </Button>

                    {!isCartValid && (
                      <p className="text-xs text-red-500 text-center mt-1">Please fill in all required fields above</p>
                    )}

                    <div className="text-center mt-3 space-y-1">
                      <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                        <Shield className="w-3 h-3" /> 256-bit SSL encrypted payment
                      </p>
                      <p className="text-xs text-gray-500">30-Day Money Back Guarantee</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SubscriptionCheckout;
